/**
 * KAGRA Budget Platform - Main Application Logic
 * Interactive Decision Platform with Real-time Actuarial Engine, Chart.js Visuals,
 * Trilingual i18n, URL State Sync, and Comprehensive Markdown/JSON Exports.
 */
import { Chart, registerables } from 'chart.js';
import {
  createIcons,
  Cpu,
  Coins,
  Share2,
  Database,
  ShieldCheck,
  HardDrive,
  Network,
  Zap,
  Award,
  Wallet,
  Calculator,
  PiggyBank,
  Activity,
  Archive,
  Wand2,
  PieChart,
  BarChart3,
  FileSpreadsheet,
  ShieldAlert,
  CheckCircle2,
  AlertCircle,
  Sliders,
  RotateCcw,
  Link,
  FileJson,
  FileText,
  Check,
  Plus,
  Minus,
  Layers,
  Lock,
  Unlock
} from 'lucide';

const usedIcons = {
  Cpu,
  Coins,
  Share2,
  Database,
  ShieldCheck,
  HardDrive,
  Network,
  Zap,
  Award,
  Wallet,
  Calculator,
  PiggyBank,
  Activity,
  Archive,
  Wand2,
  PieChart,
  BarChart3,
  FileSpreadsheet,
  ShieldAlert,
  CheckCircle2,
  AlertCircle,
  Sliders,
  RotateCcw,
  Link,
  FileJson,
  FileText,
  Check,
  Plus,
  Minus,
  Layers,
  Lock,
  Unlock
};

import {
  DEFAULT_BUDGET_NTD,
  STORAGE_TARIFFS,
  CPU_MODELS,
  GPU_MODELS,
  PRESETS,
  calculateAllocation,
  calculateAutoFillCores
} from './tariffs.js';

import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  I18N_DICT,
  CURRENCY_RATES,
  getLocale,
  setLocale,
  t,
  formatCurrency,
  formatNumber,
  getModuleInfo,
  getPresetInfo,
  getGpuInfo,
  getCpuInfo
} from './i18n.js';

// Register Chart.js components
Chart.register(...registerables);

// Chart.js global defaults for dark theme
Chart.defaults.color = '#94A3B8';
Chart.defaults.font.family = "'Inter', -apple-system, sans-serif";
Chart.defaults.plugins.tooltip.backgroundColor = 'rgba(15, 23, 42, 0.95)';
Chart.defaults.plugins.tooltip.titleColor = '#F3F4F6';
Chart.defaults.plugins.tooltip.bodyColor = '#CBD5E1';
Chart.defaults.plugins.tooltip.borderColor = 'rgba(255, 255, 255, 0.1)';
Chart.defaults.plugins.tooltip.borderWidth = 1;
Chart.defaults.plugins.tooltip.padding = 10;
Chart.defaults.plugins.tooltip.cornerRadius = 8;

/* ==========================================================================
   Application State
   ========================================================================== */
const state = {
  budget: DEFAULT_BUDGET_NTD,
  isBudgetLocked: true,
  currency: 'TWD',
  tape_tb: 1000,
  disk_tb: 200,
  osdf_disk_tb: 10,
  osdf_vcpu: 4,
  gpu_allocations: {
    A100: 16,
    L40S: 0,
    RTX4090: 0,
    RTX3090: 0,
    V100: 0,
    B6000: 0,
    P100: 0
  },
  cpu_type: 'EDR2',
  cpu_cores: 656,
  activePreset: 'preset_1',
  locale: DEFAULT_LOCALE
};

// Global chart references
let budgetDonutChart = null;
let computeBarChart = null;

/* ==========================================================================
   URL State Synchronization
   ========================================================================== */
function parseUrlState() {
  const urlParams = new URLSearchParams(window.location.search || window.location.hash.replace(/^#\/?/, ''));
  
  if (urlParams.has('lang')) {
    const lang = urlParams.get('lang');
    if (SUPPORTED_LOCALES.includes(lang)) {
      state.locale = lang;
      setLocale(lang);
    }
  }

  const curParam = urlParams.get('cur') || urlParams.get('currency');
  if (curParam && CURRENCY_RATES[curParam.toUpperCase()]) {
    state.currency = curParam.toUpperCase();
    const currencyLabel = document.getElementById('currency-label');
    if (currencyLabel) currencyLabel.textContent = `${CURRENCY_RATES[state.currency].symbol} ${state.currency}`;
  }

  if (urlParams.has('budget')) {
    const b = parseFloat(urlParams.get('budget'));
    if (Number.isFinite(b) && b > 0) state.budget = Math.max(100000, Math.min(20000000, b));
  }

  if (urlParams.has('tape')) {
    const tape = parseFloat(urlParams.get('tape'));
    if (Number.isFinite(tape)) state.tape_tb = Math.max(0, Math.min(5000, tape));
  }

  if (urlParams.has('disk')) {
    const disk = parseFloat(urlParams.get('disk'));
    if (Number.isFinite(disk)) state.disk_tb = Math.max(0, Math.min(2000, disk));
  }

  if (urlParams.has('osdf_d')) {
    const od = parseFloat(urlParams.get('osdf_d'));
    if (Number.isFinite(od)) state.osdf_disk_tb = Math.max(0, Math.min(200, od));
  }

  if (urlParams.has('osdf_c')) {
    const oc = parseInt(urlParams.get('osdf_c'), 10);
    if (Number.isFinite(oc)) state.osdf_vcpu = Math.max(1, Math.min(64, oc));
  }

  if (urlParams.has('cpu_t')) {
    const ct = urlParams.get('cpu_t');
    if (CPU_MODELS[ct]) state.cpu_type = ct;
  }

  if (urlParams.has('cpu_c')) {
    const cc = parseInt(urlParams.get('cpu_c'), 10);
    if (Number.isFinite(cc)) state.cpu_cores = Math.max(0, Math.min(10000, cc));
  }

  // Parse GPUs: either individual params gpu_A100=16 or composite gpus=A100:16,L40S:0
  for (const modelKey of Object.keys(GPU_MODELS)) {
    if (urlParams.has(`gpu_${modelKey}`)) {
      const g = parseInt(urlParams.get(`gpu_${modelKey}`), 10);
      if (Number.isFinite(g)) {
        state.gpu_allocations[modelKey] = Math.max(0, Math.min(GPU_MODELS[modelKey].max_cards, g));
      }
    }
  }

  if (urlParams.has('gpus')) {
    const pairs = urlParams.get('gpus').split(',');
    for (const pair of pairs) {
      const [key, countStr] = pair.split(':');
      if (GPU_MODELS[key]) {
        const count = parseInt(countStr, 10);
        if (Number.isFinite(count)) {
          state.gpu_allocations[key] = Math.max(0, Math.min(GPU_MODELS[key].max_cards, count));
        }
      }
    }
  }

  if (urlParams.has('preset')) {
    const p = urlParams.get('preset');
    if (PRESETS[p]) state.activePreset = p;
  }
}

function syncStateToUrl() {
  const params = new URLSearchParams();
  params.set('lang', state.locale);
  params.set('cur', state.currency);
  params.set('budget', state.budget);
  params.set('tape', state.tape_tb);
  params.set('disk', state.disk_tb);
  params.set('osdf_d', state.osdf_disk_tb);
  params.set('osdf_c', state.osdf_vcpu);
  params.set('cpu_t', state.cpu_type);
  params.set('cpu_c', state.cpu_cores);

  const gpuParts = [];
  for (const [k, v] of Object.entries(state.gpu_allocations)) {
    if (v > 0) gpuParts.push(`${k}:${v}`);
  }
  if (gpuParts.length > 0) {
    params.set('gpus', gpuParts.join(','));
  }

  if (state.activePreset) {
    params.set('preset', state.activePreset);
  }

  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState(null, '', newUrl);
}

/* ==========================================================================
   Toast Notification System
   ========================================================================== */
function showToast(message, type = 'info') {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const toast = document.createElement('div');
  toast.className = 'toast';
  
  let iconName = 'check-circle-2';
  if (type === 'warn') iconName = 'alert-circle';
  if (type === 'link') iconName = 'link';

  toast.innerHTML = `
    <i data-lucide="${iconName}" style="width: 18px; height: 18px; color: var(--color-cyan);"></i>
    <span>${message}</span>
  `;

  container.appendChild(toast);
  createIcons({ icons: usedIcons, root: toast });

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(10px)';
    toast.style.transition = 'all 0.3s ease';
    setTimeout(() => toast.remove(), 300);
  }, 3500);
}

/* ==========================================================================
   Preset Matching & Selection
   ========================================================================== */
function checkActivePreset() {
  let matchedPreset = null;

  for (const [presetId, preset] of Object.entries(PRESETS)) {
    const cfg = preset.config;
    if (
      state.tape_tb === cfg.tape_tb &&
      state.disk_tb === cfg.disk_tb &&
      state.osdf_disk_tb === cfg.osdf_disk_tb &&
      state.osdf_vcpu === cfg.osdf_vcpu &&
      state.cpu_type === cfg.cpu_type &&
      Math.abs(state.cpu_cores - cfg.cpu_cores) <= 15
    ) {
      let gpusMatch = true;
      for (const [k, v] of Object.entries(cfg.gpu_allocations)) {
        if ((state.gpu_allocations[k] || 0) !== v) {
          gpusMatch = false;
          break;
        }
      }
      if (gpusMatch) {
        matchedPreset = presetId;
        break;
      }
    }
  }

  state.activePreset = matchedPreset;
  updatePresetCardsHighlight();
}

function applyPreset(presetId) {
  const preset = PRESETS[presetId];
  if (!preset) return;

  const cfg = preset.config;
  state.tape_tb = cfg.tape_tb;
  state.disk_tb = cfg.disk_tb;
  state.osdf_disk_tb = cfg.osdf_disk_tb;
  state.osdf_vcpu = cfg.osdf_vcpu;
  state.cpu_type = cfg.cpu_type;
  state.cpu_cores = cfg.cpu_cores;

  // Reset all GPUs then apply preset values
  for (const k of Object.keys(GPU_MODELS)) {
    state.gpu_allocations[k] = cfg.gpu_allocations[k] || 0;
  }

  state.activePreset = presetId;

  // Sync inputs
  updateSliderInputs();
  updateAnalytics();
  syncStateToUrl();

  const presetName = preset[`name_${state.locale === 'zh-TW' ? 'zh' : (state.locale === 'ja' ? 'ja' : 'en')}`];
  showToast(`${t('calculator.controls.apply_preset', state.locale)}: ${presetName}`, 'info');
}

function updatePresetCardsHighlight() {
  const container = document.getElementById('presets-container');
  if (!container) return;

  const cards = container.querySelectorAll('.preset-card');
  cards.forEach(card => {
    const id = card.dataset.presetId;
    if (id === state.activePreset) {
      card.classList.add('active');
    } else {
      card.classList.remove('active');
    }
  });
}

/* ==========================================================================
   UI Component Renderers
   ========================================================================== */

/**
 * Render Presets Quick-Select Grid
 */
function renderPresets() {
  const container = document.getElementById('presets-container');
  if (!container) return;

  const locale = state.locale;
  const isZh = locale === 'zh-TW';
  const isJa = locale === 'ja';

  container.innerHTML = Object.entries(PRESETS).map(([id, p], idx) => {
    const name = p[`name_${isZh ? 'zh' : (isJa ? 'ja' : 'en')}`];
    const sub = p[`subtitle_${isZh ? 'zh' : (isJa ? 'ja' : 'en')}`];
    const tag = p[`tag_${isZh ? 'zh' : (isJa ? 'ja' : 'en')}`];
    const isActive = id === state.activePreset;

    return `
      <div class="preset-card ${isActive ? 'active' : ''}" data-preset-id="${id}">
        <div>
          <div class="preset-header">
            <span class="preset-num-badge">PRESET ${idx + 1}</span>
            ${isActive ? '<span class="mono" style="font-size: 0.68rem; color: var(--color-cyan); font-weight: 700;">ACTIVE</span>' : ''}
          </div>
          <h4 class="preset-title">${name}</h4>
          <p class="preset-subtitle">${sub}</p>
          <div class="preset-tag">
            <i data-lucide="layers" style="width: 12px; height: 12px; display: inline-block; vertical-align: middle; margin-right: 4px;"></i>
            ${tag}
          </div>
        </div>
        <button class="preset-btn" data-preset-action="${id}">
          <i data-lucide="check" style="width: 14px; height: 14px;"></i>
          <span>${t('calculator.controls.apply_preset', locale)}</span>
        </button>
      </div>
    `;
  }).join('');

  createIcons({ icons: usedIcons, root: container });

  // Attach event listeners
  container.querySelectorAll('.preset-card').forEach(card => {
    card.addEventListener('click', (e) => {
      const presetId = card.dataset.presetId;
      applyPreset(presetId);
    });
  });
}

/**
 * Render GPU Allocation Grid with Range Slider & Steppers
 */
function renderGpuCards() {
  const container = document.getElementById('gpu-cards-container');
  if (!container) return;

  const locale = state.locale;
  const isZh = locale === 'zh-TW';
  const isJa = locale === 'ja';
  const perYearUnit = isZh ? '/ 年' : (isJa ? '/ 年' : '/ yr');
  const perDayUnit = isZh ? '/日' : (isJa ? '/ 日' : '/ day');

  container.innerHTML = Object.entries(GPU_MODELS).map(([modelKey, model]) => {
    const count = state.gpu_allocations[modelKey] || 0;
    const modelTrans = getGpuInfo(modelKey, locale);
    const displayName = modelTrans.name || model.name;
    const vram = `${model.vram_gb} GB`;
    const rateYearly = formatCurrency(model.per_card_year, 'TWD', locale);

    return `
      <div class="gpu-card-item" data-model="${modelKey}">
        <div class="gpu-card-top">
          <span class="gpu-name-tag">${displayName}</span>
          <span class="gpu-vram-tag">${vram}</span>
        </div>
        <div class="gpu-rate-sub">
          <span>${rateYearly} ${perYearUnit}</span>
          <span style="opacity: 0.7;">(NT$ ${model.per_card_day}${perDayUnit})</span>
        </div>
        <div class="gpu-stepper">
          <button class="btn-step btn-minus" data-model="${modelKey}">-</button>
          <input type="number" class="gpu-num-input" data-model="${modelKey}" min="0" max="${model.max_cards}" value="${count}">
          <button class="btn-step btn-plus" data-model="${modelKey}">+</button>
          <input type="range" class="slider-input-range gpu-range-input" data-model="${modelKey}" min="0" max="${model.max_cards}" value="${count}">
        </div>
      </div>
    `;
  }).join('');

  // Attach Stepper & Slider Listeners
  container.querySelectorAll('.gpu-card-item').forEach(card => {
    const modelKey = card.dataset.model;
    const numInput = card.querySelector('.gpu-num-input');
    const rangeInput = card.querySelector('.gpu-range-input');
    const btnMinus = card.querySelector('.btn-minus');
    const btnPlus = card.querySelector('.btn-plus');

    const updateGpu = (val) => {
      let v = parseInt(val, 10);
      if (isNaN(v) || v < 0) v = 0;
      if (v > GPU_MODELS[modelKey].max_cards) v = GPU_MODELS[modelKey].max_cards;
      state.gpu_allocations[modelKey] = v;
      numInput.value = v;
      rangeInput.value = v;
      checkActivePreset();
      updateAnalytics();
      syncStateToUrl();
    };

    btnMinus.addEventListener('click', () => updateGpu((state.gpu_allocations[modelKey] || 0) - 1));
    btnPlus.addEventListener('click', () => updateGpu((state.gpu_allocations[modelKey] || 0) + 1));
    numInput.addEventListener('input', (e) => {
      if (e.target.value === '') return;
      updateGpu(e.target.value);
    });
    numInput.addEventListener('blur', (e) => {
      const v = parseInt(e.target.value, 10);
      if (isNaN(v) || v < 0) updateGpu(0);
      else updateGpu(v);
    });
    rangeInput.addEventListener('input', (e) => updateGpu(e.target.value));
  });
}

/**
 * Render CPU Architecture Selection Pills
 */
function renderCpuPills() {
  const container = document.getElementById('cpu-pills-container');
  if (!container) return;

  const locale = state.locale;
  const isZh = locale === 'zh-TW';
  const isJa = locale === 'ja';
  const perCoreUnit = isZh ? '/核·年' : (isJa ? '/コア・年' : '/core-yr');

  container.innerHTML = Object.entries(CPU_MODELS).map(([cpuKey, cpu]) => {
    const isActive = state.cpu_type === cpuKey;
    const cpuTrans = getCpuInfo(cpuKey, locale);
    const displayName = cpuTrans.name || cpu.name;
    const rateText = `NT$ ${cpu.per_core_year}${perCoreUnit}`;

    return `
      <div class="cpu-pill ${isActive ? 'active' : ''}" data-cpu-key="${cpuKey}">
        <div class="cpu-pill-name">${displayName}</div>
        <div class="cpu-pill-rate">${rateText} (${cpu.cores_per_node}C/Node)</div>
      </div>
    `;
  }).join('');

  container.querySelectorAll('.cpu-pill').forEach(pill => {
    pill.addEventListener('click', () => {
      state.cpu_type = pill.dataset.cpuKey;
      container.querySelectorAll('.cpu-pill').forEach(p => p.classList.remove('active'));
      pill.classList.add('active');
      checkActivePreset();
      updateAnalytics();
      syncStateToUrl();
    });
  });
}

/**
 * Render Official Footnotes & Compliance Disclosure
 */
function renderFootnotes() {
  const list = document.getElementById('footnotes-list');
  if (!list) return;

  const locale = state.locale;
  const footnotes = [
    { key: 'tariff_basis', title: '經費單價基準' },
    { key: 'retention_policy_basis', title: 'DocDB 17608 資料保留政策合規' },
    { key: 'attribution_clause', title: '學術論文成果致謝條款' },
    { key: 'currency_conversion', title: '國際貨幣折算說明' },
    { key: 'binary_capacity_standard', title: '二進位與十進位儲存標準規範' }
  ];

  list.innerHTML = footnotes.map(fn => {
    const text = t(`footnotes.${fn.key}`, locale);
    return `
      <li class="footnote-item">
        <span class="footnote-bullet">▸</span>
        <div>${text}</div>
      </li>
    `;
  }).join('');
}

/**
 * Update Slider Input Values in DOM
 */
function updateSliderInputs() {
  const sliderTape = document.getElementById('slider-tape');
  const sliderDisk = document.getElementById('slider-disk');
  const sliderOsdfDisk = document.getElementById('slider-osdf-disk');
  const sliderOsdfVcpu = document.getElementById('slider-osdf-vcpu');
  const sliderCpuCores = document.getElementById('slider-cpu-cores');

  if (sliderTape) sliderTape.value = state.tape_tb;
  if (sliderDisk) sliderDisk.value = state.disk_tb;
  if (sliderOsdfDisk) sliderOsdfDisk.value = state.osdf_disk_tb;
  if (sliderOsdfVcpu) sliderOsdfVcpu.value = state.osdf_vcpu;
  if (sliderCpuCores) {
    const requiredMax = Math.max(3000, Math.ceil(state.cpu_cores / 500) * 500);
    if (Number(sliderCpuCores.max) !== requiredMax) {
      sliderCpuCores.max = requiredMax;
    }
    sliderCpuCores.value = state.cpu_cores;
  }

  // Update GPU inputs
  for (const modelKey of Object.keys(GPU_MODELS)) {
    const count = state.gpu_allocations[modelKey] || 0;
    const card = document.querySelector(`.gpu-card-item[data-model="${modelKey}"]`);
    if (card) {
      const numInput = card.querySelector('.gpu-num-input');
      const rangeInput = card.querySelector('.gpu-range-input');
      if (numInput) numInput.value = count;
      if (rangeInput) rangeInput.value = count;
    }
  }

  // Update CPU Pills
  document.querySelectorAll('.cpu-pill').forEach(pill => {
    if (pill.dataset.cpuKey === state.cpu_type) {
      pill.classList.add('active');
    } else {
      pill.classList.remove('active');
    }
  });
}

/* ==========================================================================
   Chart.js Initialization & Smooth Updates
   ========================================================================== */
function initCharts() {
  // Chart 1: Donut Budget Allocation
  const ctxDonut = document.getElementById('chart-budget-donut');
  if (ctxDonut) {
    if (budgetDonutChart) {
      budgetDonutChart.destroy();
      budgetDonutChart = null;
    }
    const existingDonut = Chart.getChart(ctxDonut);
    if (existingDonut) {
      existingDonut.destroy();
    }
    budgetDonutChart = new Chart(ctxDonut, {
      type: 'doughnut',
      data: {
        labels: ['Tape Archive', 'CephFS Scratch', 'OSDF Hub', 'Dedicated GPUs', 'CPU Compute', 'Unallocated'],
        datasets: [{
          data: [300000, 200000, 11752, 700800, 287328, 120],
          backgroundColor: [
            '#6366F1', // Tape
            '#06B6D4', // CephFS
            '#10B981', // OSDF
            '#A855F7', // GPU
            '#F59E0B', // CPU
            '#334155'  // Surplus
          ],
          borderColor: '#0B0D13',
          borderWidth: 2,
          hoverOffset: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '72%',
        plugins: {
          legend: {
            position: 'right',
            labels: {
              boxWidth: 12,
              padding: 10,
              font: { size: 11, family: "'Inter', sans-serif" },
              color: '#CBD5E1'
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.raw || 0;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return ` ${context.label}: ${formatCurrency(value, state.currency, state.locale)} (${pct}%)`;
              }
            }
          }
        }
      }
    });
  }

  // Chart 2: Compute Power Balance (VRAM vs CPU Cores)
  const ctxBar = document.getElementById('chart-compute-bar');
  if (ctxBar) {
    if (computeBarChart) {
      computeBarChart.destroy();
      computeBarChart = null;
    }
    const existingBar = Chart.getChart(ctxBar);
    if (existingBar) {
      existingBar.destroy();
    }
    computeBarChart = new Chart(ctxBar, {
      type: 'bar',
      data: {
        labels: ['Total VRAM (GB)', 'CPU Cores (Count)', 'GPU Cards (Count)'],
        datasets: [{
          label: t('ui.chart_compute_density', state.locale, 'Allocated Resource Energy'),
          data: [1280, 656, 16],
          backgroundColor: [
            'rgba(168, 85, 247, 0.85)',
            'rgba(245, 158, 11, 0.85)',
            'rgba(0, 240, 255, 0.85)'
          ],
          borderColor: [
            '#A855F7',
            '#F59E0B',
            '#00F0FF'
          ],
          borderWidth: 1,
          borderRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y', // horizontal bars
        scales: {
          x: {
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: { color: '#94A3B8', font: { family: "'JetBrains Mono', monospace", size: 10 } }
          },
          y: {
            grid: { display: false },
            ticks: { color: '#E2E8F0', font: { size: 11, weight: '600' } }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: function(context) {
                return ` ${context.label}: ${formatNumber(context.raw)}`;
              }
            }
          }
        }
      }
    });
  }
}

function updateCharts(calc) {
  const locale = state.locale;
  const isZh = locale === 'zh-TW';
  const isJa = locale === 'ja';

  if (budgetDonutChart) {
    const donutLabels = [
      isZh ? '磁帶歸檔池 (Tape)' : (isJa ? 'テープアーカイブ' : 'Tape Archive'),
      isZh ? 'CephFS 工作快取 (Disk)' : (isJa ? 'CephFS 快取' : 'CephFS Scratch'),
      isZh ? 'OSDF 亞太樞紐 (Hub)' : (isJa ? 'OSDF ハブ' : 'OSDF Hub'),
      isZh ? '專屬 GPU 算力池' : (isJa ? '専有 GPU' : 'Dedicated GPUs'),
      isZh ? 'CPU 計算叢集' : (isJa ? 'CPU クラスタ' : 'CPU Compute')
    ];

    const donutData = [
      calc.tape.cost,
      calc.disk.cost,
      calc.osdf.totalCost,
      calc.gpu.totalCost,
      calc.cpu.cost
    ];

    const donutColors = [
      '#6366F1', // Tape
      '#06B6D4', // CephFS
      '#10B981', // OSDF
      '#A855F7', // GPU
      '#F59E0B'  // CPU
    ];

    if (calc.remainingBalance > 0) {
      donutLabels.push(isZh ? '未配置剩餘資金' : (isJa ? '未配分残額' : 'Surplus Balance'));
      donutData.push(calc.remainingBalance);
      donutColors.push('#334155');
    }

    budgetDonutChart.data.labels = donutLabels;
    budgetDonutChart.data.datasets[0].data = donutData;
    budgetDonutChart.data.datasets[0].backgroundColor = donutColors;
    budgetDonutChart.update('none');
  }

  if (computeBarChart) {
    const barLabels = [
      isZh ? '總顯存 VRAM (GB)' : (isJa ? '総 VRAM (GB)' : 'Total VRAM (GB)'),
      isZh ? 'CPU 總核心 (核)' : (isJa ? 'CPU 合計コア' : 'Total CPU Cores'),
      isZh ? 'GPU 總卡數 (張)' : (isJa ? 'GPU 合計枚数' : 'Total GPU Cards')
    ];

    computeBarChart.data.labels = barLabels;
    computeBarChart.data.datasets[0].label = t('ui.chart_compute_density', locale, 'Allocated Resource Energy');
    computeBarChart.data.datasets[0].data = [
      calc.gpu.totalVramGb,
      calc.cpu.cores,
      calc.gpu.totalCards
    ];
    computeBarChart.update('none');
  }
}

/* ==========================================================================
   Full Actuarial Analytics & DOM Updates
   ========================================================================== */
function updateAnalytics() {
  const calc = calculateAllocation(state);
  const locale = state.locale;
  const cur = state.currency || 'TWD';

  // 1. Update Sticky Actuarial KPI Ribbon
  const kpiTotalBudget = document.getElementById('kpi-total-budget');
  const kpiAllocated = document.getElementById('kpi-allocated');
  const kpiBalance = document.getElementById('kpi-balance');
  const kpiStatusTag = document.getElementById('kpi-status-tag');
  const kpiUtilizationVal = document.getElementById('kpi-utilization-val');
  const kpiUtilizationBar = document.getElementById('kpi-utilization-bar');
  const kpiGpuShare = document.getElementById('kpi-gpu-share');
  const kpiGpuBar = document.getElementById('kpi-gpu-bar');
  const kpiGpuCost = document.getElementById('kpi-gpu-total-cost');
  const kpiTotalVram = document.getElementById('kpi-total-vram');
  const kpiCoresSub = document.getElementById('kpi-cores-subtext');
  const kpiCoreDays = document.getElementById('kpi-core-days');

  if (kpiTotalBudget) kpiTotalBudget.textContent = formatCurrency(calc.totalBudget, cur, locale);
  if (kpiAllocated) kpiAllocated.textContent = formatCurrency(calc.totalCost, cur, locale);

  const kpiBudgetUsd = document.getElementById('kpi-budget-usd');
  const kpiBudgetJpy = document.getElementById('kpi-budget-jpy');
  if (kpiBudgetUsd) kpiBudgetUsd.textContent = `≈ ${formatCurrency(calc.totalBudget, 'USD', locale)} USD`;
  if (kpiBudgetJpy) kpiBudgetJpy.textContent = `≈ ${formatCurrency(calc.totalBudget, 'JPY', locale)} JPY`;
  
  if (kpiBalance) {
    kpiBalance.textContent = formatCurrency(Math.abs(calc.remainingBalance), cur, locale);
    const balanceCard = document.getElementById('kpi-balance-card');
    const balanceBar = document.getElementById('kpi-balance-bar');

    if (calc.remainingBalance < 0) {
      kpiBalance.textContent = `- ${formatCurrency(Math.abs(calc.remainingBalance), cur, locale)}`;
      kpiBalance.style.color = 'var(--color-rose)';
      if (kpiStatusTag) kpiStatusTag.textContent = t('calculator.status_messages.deficit.title', locale);
      if (balanceCard) balanceCard.className = 'kpi-card rose';
      if (balanceBar) {
        balanceBar.style.width = '100%';
        balanceBar.style.background = 'var(--color-rose)';
      }
    } else if (calc.isOptimal) {
      kpiBalance.style.color = 'var(--color-emerald)';
      if (kpiStatusTag) kpiStatusTag.textContent = t('calculator.status_messages.optimal.title', locale);
      if (balanceCard) balanceCard.className = 'kpi-card emerald';
      if (balanceBar) {
        balanceBar.style.width = '100%';
        balanceBar.style.background = 'var(--color-emerald)';
      }
    } else {
      kpiBalance.style.color = 'var(--color-cyan)';
      if (kpiStatusTag) kpiStatusTag.textContent = t('calculator.status_messages.surplus.title', locale);
      if (balanceCard) balanceCard.className = 'kpi-card cyan';
      if (balanceBar) {
        balanceBar.style.width = '70%';
        balanceBar.style.background = 'var(--color-cyan)';
      }
    }
  }

  if (kpiUtilizationVal) {
    kpiUtilizationVal.textContent = `${calc.utilizationPct.toFixed(1)}%`;
  }
  if (kpiUtilizationBar) {
    kpiUtilizationBar.style.width = `${Math.min(100, calc.utilizationPct)}%`;
    kpiUtilizationBar.style.background = calc.totalCost > calc.totalBudget ? 'var(--color-rose)' : 'var(--color-indigo)';
  }

  if (kpiGpuShare) {
    kpiGpuShare.textContent = `${calc.gpu.pct.toFixed(1)}%`;
    kpiGpuShare.style.color = calc.gpuTargetMet ? 'var(--color-purple)' : 'var(--color-amber)';
  }
  if (kpiGpuBar) {
    kpiGpuBar.style.width = `${Math.min(100, calc.gpu.pct)}%`;
    kpiGpuBar.style.background = calc.gpuTargetMet ? 'var(--color-purple)' : 'var(--color-amber)';
  }
  if (kpiGpuCost) {
    kpiGpuCost.textContent = formatCurrency(calc.gpu.totalCost, cur, locale);
  }

  if (kpiTotalVram) {
    kpiTotalVram.textContent = `${formatNumber(calc.gpu.totalVramGb)} GB`;
  }
  if (kpiCoresSub) {
    kpiCoresSub.textContent = `CPU: ${formatNumber(calc.cpu.cores)} ${locale === 'en' ? 'Cores' : (locale === 'ja' ? 'コア' : '核心')}`;
  }
  if (kpiCoreDays) {
    const kCoreDays = (calc.cpu.coreDays / 10000).toFixed(1);
    kpiCoreDays.textContent = locale === 'en' ? `${(calc.cpu.coreDays / 1000).toFixed(0)}k core-days/yr` : `${kCoreDays} 萬核·日/年`;
  }

  // 2. Update Module Slider Badges & Stats Bars
  // Module 1: Tape
  const valTapeTb = document.getElementById('val-tape-tb');
  const valTapePb = document.getElementById('val-tape-pb');
  const statTapeCost = document.getElementById('stat-tape-cost');
  const statTapePct = document.getElementById('stat-tape-pct');
  if (valTapeTb) valTapeTb.textContent = `${formatNumber(calc.tape.tb)} TB`;
  if (valTapePb) valTapePb.textContent = `(${calc.tape.pb.toFixed(2)} PB)`;
  if (statTapeCost) statTapeCost.textContent = formatCurrency(calc.tape.cost, cur, locale);
  if (statTapePct) statTapePct.textContent = `${calc.tape.pct.toFixed(1)}%`;

  // Module 2: Disk
  const valDiskTb = document.getElementById('val-disk-tb');
  const valDiskPb = document.getElementById('val-disk-pb');
  const statDiskCost = document.getElementById('stat-disk-cost');
  const statDiskPct = document.getElementById('stat-disk-pct');
  if (valDiskTb) valDiskTb.textContent = `${formatNumber(calc.disk.tb)} TB`;
  if (valDiskPb) valDiskPb.textContent = `(${calc.disk.pb.toFixed(2)} PB)`;
  if (statDiskCost) statDiskCost.textContent = formatCurrency(calc.disk.cost, cur, locale);
  if (statDiskPct) statDiskPct.textContent = `${calc.disk.pct.toFixed(1)}%`;

  // Module 3: OSDF
  const valOsdfDiskTb = document.getElementById('val-osdf-disk-tb');
  const valOsdfVcpu = document.getElementById('val-osdf-vcpu');
  const statOsdfCost = document.getElementById('stat-osdf-cost');
  const statOsdfPct = document.getElementById('stat-osdf-pct');
  if (valOsdfDiskTb) valOsdfDiskTb.textContent = `${calc.osdf.diskTb} TB`;
  if (valOsdfVcpu) valOsdfVcpu.textContent = `${calc.osdf.vcpu} vCPU`;
  if (statOsdfCost) statOsdfCost.textContent = formatCurrency(calc.osdf.totalCost, cur, locale);
  if (statOsdfPct) statOsdfPct.textContent = `${calc.osdf.pct.toFixed(1)}%`;

  // Module 4: Compute
  const gpuTotalSummary = document.getElementById('gpu-total-summary');
  const valCpuCores = document.getElementById('val-cpu-cores');
  const valCpuCday = document.getElementById('val-cpu-cday');
  const statComputeCost = document.getElementById('stat-compute-cost');
  const statComputePct = document.getElementById('stat-compute-pct');

  if (gpuTotalSummary) {
    gpuTotalSummary.textContent = locale === 'en' 
      ? `Total: ${calc.gpu.totalCards} cards | ${calc.gpu.totalVramGb} GB VRAM`
      : (locale === 'ja'
        ? `合計 ${calc.gpu.totalCards} 枚 | ${calc.gpu.totalVramGb} GB VRAM`
        : `總計 ${calc.gpu.totalCards} 卡 | ${calc.gpu.totalVramGb} GB VRAM`);
  }
  if (valCpuCores) {
    valCpuCores.textContent = `${formatNumber(calc.cpu.cores)} ${locale === 'en' ? 'Cores' : (locale === 'ja' ? 'コア' : '核')}`;
  }
  if (valCpuCday) {
    valCpuCday.textContent = locale === 'en'
      ? `(${(calc.cpu.coreDays / 1000).toFixed(0)}k core-days)`
      : `(${(calc.cpu.coreDays / 10000).toFixed(1)} 萬核·日)`;
  }
  if (statComputeCost) {
    statComputeCost.textContent = formatCurrency(calc.computeSubtotal.cost, cur, locale);
  }
  if (statComputePct) {
    statComputePct.textContent = `${calc.computeSubtotal.pct.toFixed(1)}%`;
  }

  // 3. Update Auto-fill Button Preview
  const autoCores = calculateAutoFillCores(state);
  const autoFillPreview = document.getElementById('btn-auto-fill-preview');
  if (autoFillPreview) {
    const diff = autoCores - state.cpu_cores;
    const coreUnit = locale === 'en' ? 'Cores' : (locale === 'ja' ? 'コア' : '核');
    autoFillPreview.textContent = diff >= 0 ? `(+${diff} ${coreUnit})` : `(${diff} ${coreUnit})`;
    if (diff === 0) autoFillPreview.textContent = t('ui.units.balanced_tag', locale, '(已達最佳平衡)');
  }

  // 4. Update Detailed Financial Breakdown Table
  renderFinancialTable(calc);

  // 5. Update Status Banner
  const banner = document.getElementById('status-banner');
  const bannerText = document.getElementById('status-banner-text');
  if (banner && bannerText) {
    if (calc.totalCost > calc.totalBudget) {
      banner.className = 'status-banner deficit';
      bannerText.innerHTML = `<strong>${t('calculator.status_messages.deficit.title', locale)}</strong>：${t('calculator.status_messages.deficit.text', locale)}`;
    } else if (calc.isOptimal) {
      banner.className = 'status-banner optimal';
      bannerText.innerHTML = `<strong>${t('calculator.status_messages.optimal.title', locale)}</strong>：${t('calculator.status_messages.optimal.text', locale)}`;
    } else {
      banner.className = 'status-banner surplus';
      bannerText.innerHTML = `<strong>${t('calculator.status_messages.surplus.title', locale)}</strong>：${t('calculator.status_messages.surplus.text', locale)}`;
    }
  }

  // 6. Update Strategic Checklist Icons
  const chkDocdbIcon = document.getElementById('check-icon-docdb');
  const chkCephIcon = document.getElementById('check-icon-ceph');
  const chkGpuIcon = document.getElementById('check-icon-gpu');

  if (chkDocdbIcon) {
    chkDocdbIcon.className = calc.tape.tb <= 1000 ? 'check-icon pass' : 'check-icon warn';
    chkDocdbIcon.textContent = calc.tape.tb <= 1000 ? '✓' : '!';
  }
  if (chkCephIcon) {
    chkCephIcon.className = calc.disk.tb <= 200 ? 'check-icon pass' : 'check-icon warn';
    chkCephIcon.textContent = calc.disk.tb <= 200 ? '✓' : '!';
  }
  if (chkGpuIcon) {
    chkGpuIcon.className = calc.gpuTargetMet ? 'check-icon pass' : 'check-icon warn';
    chkGpuIcon.textContent = calc.gpuTargetMet ? '✓' : '!';
  }

  // 7. Update Charts
  updateCharts(calc);
}

/**
 * Render Financial Table rows
 */
function renderFinancialTable(calc) {
  const tbody = document.getElementById('table-body');
  if (!tbody) return;

  const locale = state.locale;
  const isZh = locale === 'zh-TW';
  const isJa = locale === 'ja';
  const cur = state.currency || 'TWD';
  const cardUnit = isZh ? '卡' : (isJa ? '枚' : 'cards');
  const cardSingle = isZh ? '卡' : (isJa ? '枚' : 'card');
  const coreUnit = isZh ? '核' : (isJa ? 'コア' : 'cores');
  const coreSingle = isZh ? '核' : (isJa ? 'コア' : 'core');
  const gpuUnit = isZh ? '卡' : (isJa ? '枚' : 'GPUs');
  const cpuUnit = isZh ? '核' : (isJa ? 'コア' : 'Cores');

  const tapeRowTitle = isZh ? 'LTO 磁帶歸檔池 (1.0 PB)' : (isJa ? 'LTO テープアーカイブ (1.0 PB)' : 'LTO Tape Archive (1.0 PB)');
  const cephRowTitle = isZh ? 'CephFS 工作快取池' : (isJa ? 'CephFS ワークキャッシュ' : 'CephFS Scratch Cache');
  const osdfDiskRowTitle = isZh ? 'OSDF 快取磁碟' : (isJa ? 'OSDF キャッシュディスク' : 'OSDF Cache Disk');
  const osdfCpuRowTitle = isZh ? 'OSDF 代理節點 vCPU' : (isJa ? 'OSDF プロキシノード vCPU' : 'OSDF Proxy vCPU');

  const rows = [];

  const currencyTag = document.getElementById('table-currency-tag');
  if (currencyTag) currencyTag.textContent = `${cur} / Year`;

  // Tape Row
  rows.push(`
    <tr>
      <td><span class="tier-dot dot-tape"></span>${tapeRowTitle}</td>
      <td class="mono">NT$ 300 / TB</td>
      <td class="mono">${formatNumber(calc.tape.tb)} TB</td>
      <td class="mono">${formatCurrency(calc.tape.cost, cur, locale)}</td>
      <td class="mono">${calc.tape.pct.toFixed(1)}%</td>
    </tr>
  `);

  // CephFS Row
  rows.push(`
    <tr>
      <td><span class="tier-dot dot-disk"></span>${cephRowTitle}</td>
      <td class="mono">NT$ 1,000 / TB</td>
      <td class="mono">${formatNumber(calc.disk.tb)} TB</td>
      <td class="mono">${formatCurrency(calc.disk.cost, cur, locale)}</td>
      <td class="mono">${calc.disk.pct.toFixed(1)}%</td>
    </tr>
  `);

  // OSDF Rows
  rows.push(`
    <tr>
      <td><span class="tier-dot dot-osdf"></span>${osdfDiskRowTitle}</td>
      <td class="mono">NT$ 1,000 / TB</td>
      <td class="mono">${calc.osdf.diskTb} TB</td>
      <td class="mono">${formatCurrency(calc.osdf.diskCost, cur, locale)}</td>
      <td class="mono">${((calc.osdf.diskCost / calc.totalBudget) * 100).toFixed(1)}%</td>
    </tr>
    <tr>
      <td><span class="tier-dot dot-osdf"></span>${osdfCpuRowTitle} (${calc.osdf.cpuType})</td>
      <td class="mono">NT$ ${CPU_MODELS[calc.osdf.cpuType]?.per_core_year || 438} / vCPU</td>
      <td class="mono">${calc.osdf.vcpu} vCPU</td>
      <td class="mono">${formatCurrency(calc.osdf.cpuCost, cur, locale)}</td>
      <td class="mono">${((calc.osdf.cpuCost / calc.totalBudget) * 100).toFixed(1)}%</td>
    </tr>
  `);

  // GPU Rows
  for (const g of calc.gpu.details) {
    const gpuTrans = getGpuInfo(g.id, locale);
    const gpuDisplayName = gpuTrans.name || g.name;
    rows.push(`
      <tr>
        <td><span class="tier-dot dot-gpu"></span>GPU: ${gpuDisplayName} (${g.vram_gb_single}GB)</td>
        <td class="mono">NT$ ${formatNumber(g.per_card_year)} / ${cardSingle}</td>
        <td class="mono">${g.count} ${cardUnit} (${g.vram_gb_total} GB)</td>
        <td class="mono">${formatCurrency(g.total_cost, cur, locale)}</td>
        <td class="mono">${((g.total_cost / calc.totalBudget) * 100).toFixed(1)}%</td>
      </tr>
    `);
  }

  // CPU Row
  rows.push(`
    <tr>
      <td><span class="tier-dot dot-cpu"></span>CPU: ${calc.cpu.name}</td>
      <td class="mono">NT$ ${calc.cpu.unitPrice} / ${coreSingle}</td>
      <td class="mono">${formatNumber(calc.cpu.cores)} ${coreUnit}</td>
      <td class="mono">${formatCurrency(calc.cpu.cost, cur, locale)}</td>
      <td class="mono">${calc.cpu.pct.toFixed(1)}%</td>
    </tr>
  `);

  // Storage Subtotal
  rows.push(`
    <tr style="background: rgba(255, 255, 255, 0.03); font-weight: 700;">
      <td>${t('ui.subtotals.storage', locale, 'Subtotal: Base Storage & Scratch')}</td>
      <td class="mono">-</td>
      <td class="mono">${formatNumber(calc.tape.tb + calc.disk.tb + calc.osdf.diskTb)} TB</td>
      <td class="mono" style="color: var(--color-cyan);">${formatCurrency(calc.storageSubtotal.cost, cur, locale)}</td>
      <td class="mono">${calc.storageSubtotal.pct.toFixed(1)}%</td>
    </tr>
  `);

  // Compute Subtotal
  rows.push(`
    <tr style="background: rgba(168, 85, 247, 0.08); font-weight: 700;">
      <td>${t('ui.subtotals.compute', locale, 'Subtotal: Core Compute Pool (GPU + CPU)')}</td>
      <td class="mono">-</td>
      <td class="mono">${calc.gpu.totalCards} ${gpuUnit} / ${formatNumber(calc.cpu.cores)} ${cpuUnit}</td>
      <td class="mono" style="color: var(--color-purple);">${formatCurrency(calc.computeSubtotal.cost, cur, locale)}</td>
      <td class="mono">${calc.computeSubtotal.pct.toFixed(1)}%</td>
    </tr>
  `);

  tbody.innerHTML = rows.join('');
}

/* ==========================================================================
   Language Switching & Static Texts Hydration
   ========================================================================== */
function switchLocale(newLocale) {
  if (!SUPPORTED_LOCALES.includes(newLocale)) return;
  state.locale = newLocale;
  setLocale(newLocale);

  // Update active pill button
  document.querySelectorAll('.lang-btn').forEach(btn => {
    if (btn.dataset.lang === newLocale) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });

  // Rehydrate Header & Hero Texts
  const brandTitle = document.getElementById('brand-title');
  const heroTitle = document.getElementById('hero-title');
  const heroTagline = document.getElementById('hero-tagline');
  const heroDeclaration = document.getElementById('hero-declaration');

  if (brandTitle) brandTitle.textContent = t('system.title', newLocale);
  if (heroTitle) heroTitle.textContent = t('system.title', newLocale);
  if (heroTagline) heroTagline.textContent = t('system.tagline', newLocale);
  if (heroDeclaration) heroDeclaration.textContent = t('system.strategic_declaration', newLocale);

  // Update Hero Badges
  const heroBadges = document.getElementById('hero-badges');
  if (heroBadges) {
    heroBadges.innerHTML = `
      <span class="badge-tag highlight"><i data-lucide="database" style="width: 13px; height: 13px;"></i> ${t('system.badges.tape_cap', newLocale)}</span>
      <span class="badge-tag cyan"><i data-lucide="shield-check" style="width: 13px; height: 13px;"></i> ${t('system.badges.retention_policy', newLocale)}</span>
      <span class="badge-tag"><i data-lucide="hard-drive" style="width: 13px; height: 13px;"></i> ${t('system.badges.disk_cache', newLocale)}</span>
      <span class="badge-tag emerald"><i data-lucide="network" style="width: 13px; height: 13px;"></i> ${t('system.badges.osdf_hub', newLocale)}</span>
      <span class="badge-tag cyan"><i data-lucide="zap" style="width: 13px; height: 13px;"></i> ${t('system.badges.gpu_priority', newLocale)}</span>
      <span class="badge-tag highlight"><i data-lucide="award" style="width: 13px; height: 13px;"></i> ${t('system.badges.credit_ack', newLocale)}</span>
    `;
    createIcons({ icons: usedIcons, root: heroBadges });
  }

  // Update section titles
  const presetsHeading = document.getElementById('presets-heading');
  const modulesHeading = document.getElementById('modules-heading');
  const analyticsHeading = document.getElementById('analytics-heading');
  if (presetsHeading) presetsHeading.textContent = t('ui.presets_heading', newLocale);
  if (modulesHeading) modulesHeading.textContent = t('ui.modules_heading', newLocale);
  if (analyticsHeading) analyticsHeading.textContent = t('ui.analytics_heading', newLocale);

  // Update KPI Ribbon Labels
  const kpiLabelBudget = document.getElementById('kpi-label-budget');
  const kpiLabelAllocated = document.getElementById('kpi-label-allocated');
  const kpiLabelBalance = document.getElementById('kpi-label-balance');
  const kpiLabelGpuShare = document.getElementById('kpi-label-gpu-share');
  const kpiGpuTarget = document.getElementById('kpi-gpu-target');
  const kpiLabelComputePower = document.getElementById('kpi-label-compute-power');
  const kpiUtilizationLabel = document.getElementById('kpi-utilization-label');

  if (kpiLabelBudget) kpiLabelBudget.textContent = t('calculator.labels.total_budget', newLocale);
  if (kpiLabelAllocated) kpiLabelAllocated.textContent = t('calculator.labels.allocated_budget', newLocale);
  if (kpiLabelBalance) kpiLabelBalance.textContent = t('calculator.labels.remaining_balance', newLocale);
  if (kpiLabelGpuShare) kpiLabelGpuShare.textContent = t('calculator.labels.gpu_share', newLocale);
  if (kpiGpuTarget) kpiGpuTarget.textContent = t('ui.kpi_gpu_target', newLocale);
  if (kpiLabelComputePower) kpiLabelComputePower.textContent = t('ui.kpi_total_compute', newLocale);
  if (kpiUtilizationLabel) kpiUtilizationLabel.textContent = t('calculator.labels.budget_utilization', newLocale);

  // Update Module Headings & Descriptions & Tariffs
  const modTape = getModuleInfo('tape_archive', newLocale);
  const modCeph = getModuleInfo('cephfs_cache', newLocale);
  const modOsdf = getModuleInfo('osdf_hub', newLocale);
  const modCompute = getModuleInfo('compute_pool', newLocale);

  if (document.getElementById('mod-tape-title')) document.getElementById('mod-tape-title').textContent = modTape.name || 'Tape Archive';
  if (document.getElementById('mod-tape-cat')) document.getElementById('mod-tape-cat').textContent = modTape.category || '';
  if (document.getElementById('mod-tape-tariff')) document.getElementById('mod-tape-tariff').textContent = modTape.pricing_tag || 'NT$ 300 / TB / yr';
  if (document.getElementById('mod-tape-desc')) document.getElementById('mod-tape-desc').textContent = modTape.description || '';

  if (document.getElementById('mod-ceph-title')) document.getElementById('mod-ceph-title').textContent = modCeph.name || 'CephFS Scratch';
  if (document.getElementById('mod-ceph-cat')) document.getElementById('mod-ceph-cat').textContent = modCeph.category || '';
  if (document.getElementById('mod-ceph-tariff')) document.getElementById('mod-ceph-tariff').textContent = modCeph.pricing_tag || 'NT$ 1,000 / TB / yr';
  if (document.getElementById('mod-ceph-desc')) document.getElementById('mod-ceph-desc').textContent = modCeph.description || '';

  if (document.getElementById('mod-osdf-title')) document.getElementById('mod-osdf-title').textContent = modOsdf.name || 'OSDF APAC Hub';
  if (document.getElementById('mod-osdf-cat')) document.getElementById('mod-osdf-cat').textContent = modOsdf.category || '';
  if (document.getElementById('mod-osdf-tariff')) document.getElementById('mod-osdf-tariff').textContent = modOsdf.pricing_tag || 'Disk $1,000 + vCPU Tariff';
  if (document.getElementById('mod-osdf-desc')) document.getElementById('mod-osdf-desc').textContent = modOsdf.description || '';

  if (document.getElementById('mod-compute-title')) document.getElementById('mod-compute-title').textContent = modCompute.name || 'Core Compute';
  if (document.getElementById('mod-compute-cat')) document.getElementById('mod-compute-cat').textContent = modCompute.category || '';
  if (document.getElementById('mod-compute-tariff')) document.getElementById('mod-compute-tariff').textContent = modCompute.pricing_tag || 'Dynamic Balancing (>65%)';
  if (document.getElementById('mod-compute-desc')) document.getElementById('mod-compute-desc').textContent = modCompute.description || '';

  // Update Slider Labels
  if (document.getElementById('label-tape-slider')) document.getElementById('label-tape-slider').textContent = t('ui.tape_slider', newLocale);
  if (document.getElementById('label-disk-slider')) document.getElementById('label-disk-slider').textContent = t('ui.disk_slider', newLocale);
  if (document.getElementById('label-osdf-disk')) document.getElementById('label-osdf-disk').textContent = t('ui.osdf_disk_slider', newLocale);
  if (document.getElementById('label-osdf-vcpu')) document.getElementById('label-osdf-vcpu').textContent = t('ui.osdf_vcpu_slider', newLocale);
  if (document.getElementById('gpu-alloc-heading')) {
    document.getElementById('gpu-alloc-heading').innerHTML = `<i data-lucide="cpu" style="width: 14px; height: 14px; display: inline-block; vertical-align: middle;"></i> ${t('ui.gpu_alloc_heading', newLocale)}`;
    createIcons({ icons: usedIcons, root: document.getElementById('gpu-alloc-heading') });
  }
  if (document.getElementById('cpu-arch-heading')) document.getElementById('cpu-arch-heading').textContent = t('ui.cpu_arch_heading', newLocale);
  if (document.getElementById('label-cpu-cores')) document.getElementById('label-cpu-cores').textContent = t('ui.cpu_cores_slider', newLocale);

  // Update Slider Scale Hints
  if (document.getElementById('scale-tape-docdb')) document.getElementById('scale-tape-docdb').textContent = t('ui.scales.tape_docdb', newLocale);
  if (document.getElementById('scale-disk-rec')) document.getElementById('scale-disk-rec').textContent = t('ui.scales.disk_rec', newLocale);
  if (document.getElementById('scale-osdf-base')) document.getElementById('scale-osdf-base').textContent = t('ui.scales.osdf_base', newLocale);
  if (document.getElementById('scale-osdf-mirror')) document.getElementById('scale-osdf-mirror').textContent = t('ui.scales.osdf_mirror', newLocale);
  if (document.getElementById('scale-osdf-mid')) document.getElementById('scale-osdf-mid').textContent = t('ui.scales.osdf_mid', newLocale);
  if (document.getElementById('scale-osdf-max')) document.getElementById('scale-osdf-max').textContent = t('ui.scales.osdf_max', newLocale);
  if (document.getElementById('scale-cpu-mid')) document.getElementById('scale-cpu-mid').textContent = t('ui.scales.cpu_mid', newLocale);

  // Update Module Stat Labels
  const statCostText = t('ui.stat_cost_label', newLocale);
  const statPctText = t('ui.stat_pct_label', newLocale);
  const statComputeCostText = t('ui.stat_compute_cost_label', newLocale);
  if (document.getElementById('stat-tape-cost-label')) document.getElementById('stat-tape-cost-label').textContent = statCostText;
  if (document.getElementById('stat-tape-pct-label')) document.getElementById('stat-tape-pct-label').textContent = statPctText;
  if (document.getElementById('stat-disk-cost-label')) document.getElementById('stat-disk-cost-label').textContent = statCostText;
  if (document.getElementById('stat-disk-pct-label')) document.getElementById('stat-disk-pct-label').textContent = statPctText;
  if (document.getElementById('stat-osdf-cost-label')) document.getElementById('stat-osdf-cost-label').textContent = statCostText;
  if (document.getElementById('stat-osdf-pct-label')) document.getElementById('stat-osdf-pct-label').textContent = statPctText;
  if (document.getElementById('stat-compute-cost-label')) document.getElementById('stat-compute-cost-label').textContent = statComputeCostText;
  if (document.getElementById('stat-compute-pct-label')) document.getElementById('stat-compute-pct-label').textContent = statPctText;

  // Update Chart Titles
  if (document.getElementById('chart1-title')) document.getElementById('chart1-title').textContent = t('ui.chart1_title', newLocale);
  if (document.getElementById('chart2-title')) document.getElementById('chart2-title').textContent = t('ui.chart2_title', newLocale);

  // Update Table Title & Headers
  if (document.getElementById('table-title')) document.getElementById('table-title').textContent = t('ui.table_title', newLocale);
  if (document.getElementById('th-item')) document.getElementById('th-item').textContent = t('ui.table_headers.item', newLocale);
  if (document.getElementById('th-rate')) document.getElementById('th-rate').textContent = t('ui.table_headers.rate', newLocale);
  if (document.getElementById('th-alloc')) document.getElementById('th-alloc').textContent = t('ui.table_headers.alloc', newLocale);
  if (document.getElementById('th-cost')) document.getElementById('th-cost').textContent = t('ui.table_headers.cost', newLocale);
  if (document.getElementById('th-share')) document.getElementById('th-share').textContent = t('ui.table_headers.share', newLocale);

  // Update Checklist Texts
  if (document.getElementById('checklist-title')) document.getElementById('checklist-title').textContent = t('ui.checklist_title', newLocale);
  if (document.getElementById('chk-docdb-title')) document.getElementById('chk-docdb-title').textContent = t('ui.checklist.docdb_title', newLocale);
  if (document.getElementById('chk-docdb-desc')) document.getElementById('chk-docdb-desc').textContent = t('ui.checklist.docdb_desc', newLocale);
  if (document.getElementById('chk-ceph-title')) document.getElementById('chk-ceph-title').textContent = t('ui.checklist.ceph_title', newLocale);
  if (document.getElementById('chk-ceph-desc')) document.getElementById('chk-ceph-desc').textContent = t('ui.checklist.ceph_desc', newLocale);
  if (document.getElementById('chk-gpu-title')) document.getElementById('chk-gpu-title').textContent = t('ui.checklist.gpu_title', newLocale);
  if (document.getElementById('chk-gpu-desc')) document.getElementById('chk-gpu-desc').textContent = t('ui.checklist.gpu_desc', newLocale);
  if (document.getElementById('chk-attrib-title')) document.getElementById('chk-attrib-title').textContent = t('ui.checklist.attrib_title', newLocale);
  if (document.getElementById('chk-attrib-desc')) document.getElementById('chk-attrib-desc').textContent = t('ui.checklist.attrib_desc', newLocale);

  // Update Action Dock Titles & Buttons
  if (document.getElementById('action-dock-title')) document.getElementById('action-dock-title').textContent = t('ui.action_dock.title', newLocale);
  if (document.getElementById('action-dock-subtitle')) document.getElementById('action-dock-subtitle').textContent = t('ui.action_dock.subtitle', newLocale);
  const btnShareText = document.getElementById('btn-share-text');
  const btnAutoFillText = document.getElementById('btn-auto-fill-text');
  const btnResetText = document.getElementById('btn-reset-text');
  const btnCopyUrlText = document.getElementById('btn-copy-url-text');
  const btnExportJsonText = document.getElementById('btn-export-json-text');
  const btnExportMdText = document.getElementById('btn-export-md-text');

  if (btnShareText) btnShareText.textContent = newLocale === 'zh-TW' ? '分享連結' : (newLocale === 'ja' ? '共有' : 'Share');
  if (btnAutoFillText) btnAutoFillText.textContent = t('calculator.controls.auto_fill_cpu', newLocale);
  if (btnResetText) btnResetText.textContent = t('calculator.controls.reset_defaults', newLocale);
  if (btnCopyUrlText) btnCopyUrlText.textContent = t('actions.copy_share_url', newLocale);
  if (btnExportJsonText) btnExportJsonText.textContent = t('actions.export_json', newLocale);
  if (btnExportMdText) btnExportMdText.textContent = t('actions.export_summary_md', newLocale);

  // Update Footnotes Title
  if (document.getElementById('footnotes-title')) document.getElementById('footnotes-title').textContent = t('ui.footnotes_title', newLocale);

  // Re-render dynamic elements
  renderPresets();
  renderGpuCards();
  renderCpuPills();
  renderFootnotes();
  updateAnalytics();
  syncStateToUrl();
}

/* ==========================================================================
   Export Functionalities (JSON & Markdown)
   ========================================================================== */

/**
 * Export Actuarial Configuration as Clean JSON
 */
function exportJSON() {
  const calc = calculateAllocation(state);
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 10);

  const payload = {
    metadata: {
      project: "KAGRA Budget Optimization & Computing Transformation",
      system: "KAGRA Budget Allocation Interactive Decision Platform",
      generated_at: now.toISOString(),
      governance: {
        pi: "Prof. Henry Tsz-King Wong (ASIoP)",
        infrastructure_partner: "ASGC (Academia Sinica Grid-computing Centre)",
        policy: "DocDB 17608 (KAGRA Data Retention Policy)",
        pricing_source: "NSTCCore / ASGC 2026 Official Tariffs",
        attribution_clause: "Computed at Academia Sinica (AS)"
      }
    },
    actuarial_summary: {
      total_budget_ntd: calc.totalBudget,
      total_cost_ntd: calc.totalCost,
      remaining_balance_ntd: calc.remainingBalance,
      utilization_percentage: calc.utilizationPct,
      gpu_share_percentage: calc.gpu.pct,
      is_valid: calc.isValid,
      is_optimal: calc.isOptimal,
      gpu_target_met: calc.gpuTargetMet,
      retention_compliant: calc.retentionCompliant
    },
    resource_allocations: {
      tape_archive: {
        capacity_tb: calc.tape.tb,
        capacity_pb: calc.tape.pb,
        unit_price_tb_year: calc.tape.unitPrice,
        annual_cost_ntd: calc.tape.cost,
        share_percentage: calc.tape.pct
      },
      cephfs_scratch: {
        capacity_tb: calc.disk.tb,
        capacity_pb: calc.disk.pb,
        unit_price_tb_year: calc.disk.unitPrice,
        annual_cost_ntd: calc.disk.cost,
        share_percentage: calc.disk.pct
      },
      osdf_apac_hub: {
        cache_disk_tb: calc.osdf.diskTb,
        proxy_vcpu_count: calc.osdf.vcpu,
        proxy_cpu_architecture: calc.osdf.cpuType,
        disk_cost_ntd: calc.osdf.diskCost,
        vcpu_cost_ntd: calc.osdf.cpuCost,
        total_annual_cost_ntd: calc.osdf.totalCost,
        share_percentage: calc.osdf.pct
      },
      gpu_cluster: {
        total_cards: calc.gpu.totalCards,
        total_vram_gb: calc.gpu.totalVramGb,
        total_annual_cost_ntd: calc.gpu.totalCost,
        share_percentage: calc.gpu.pct,
        models_breakdown: calc.gpu.details
      },
      cpu_cluster: {
        architecture: calc.cpu.name,
        cores_count: calc.cpu.cores,
        annual_core_days: calc.cpu.coreDays,
        unit_price_core_year: calc.cpu.unitPrice,
        total_annual_cost_ntd: calc.cpu.cost,
        share_percentage: calc.cpu.pct
      }
    }
  };

  const jsonStr = JSON.stringify(payload, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `kagra-budget-allocation-${timestamp}.json`;
  a.click();
  URL.revokeObjectURL(url);

  showToast(t('actions.exported_notification', state.locale), 'info');
}

/**
 * Export Executive Decision Brief as Markdown
 */
function exportMarkdown() {
  const calc = calculateAllocation(state);
  const locale = state.locale;
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 10);
  const isZh = locale === 'zh-TW';
  const isJa = locale === 'ja';

  let md = '';

  if (isZh) {
    md += `# KAGRA 經費配置與算力轉型戰略決策備忘錄 (Decision Brief)\n\n`;
    md += `> **產製日期**：${timestamp}  \n`;
    md += `> **主責主持**：中研院物理所（ASIoP）王子敬教授  \n`;
    md += `> **技術合作**：ASGC 網格計算中心（IT Operation / Felix Lee / 翔宇 Patrick）  \n`;
    md += `> **國際對接**：KAGRA 合作組（Nobuyuki Kanda 神田展行 / Takahiro Sawada 澤田崇弘 / Alvin 團隊）  \n`;
    md += `> **政策合規**：KAGRA DocDB 17608（2026-10-29 KSC 全會一致通過新資料保留政策）  \n`;
    md += `> **牌價基準**：國家科學技術委員會核心設施（NSTCCore / ASGC 2026 官方公開標準牌價）  \n\n`;

    md += `## 一、 決策執行摘要 (Executive Summary)\n\n`;
    md += `- **年度總預算**：NT$ ${formatNumber(calc.totalBudget)} / 年（折合約 46,000 USD / 700 萬 JPY）\n`;
    md += `- **已配置總支出**：**NT$ ${formatNumber(calc.totalCost)}** / 年（預算執行率：**${calc.utilizationPct.toFixed(2)}%**）\n`;
    md += `- **剩餘待配置資金**：NT$ ${formatNumber(calc.remainingBalance)}（${calc.isOptimal ? '精準平衡零浪費' : (calc.remainingBalance < 0 ? '超額超支' : '尚有結餘')}）\n`;
    md += `- **GPU 戰略轉型佔比**：**${calc.gpu.pct.toFixed(2)}%**（NT$ ${formatNumber(calc.gpu.totalCost)}，${calc.gpuTargetMet ? '✅ 達成 >65% 優先算力指標' : '⚠️ 低於 65% 建議門檻'}）\n`;
    md += `- **總算力交付規模**：**${calc.gpu.totalCards} 張高階 GPU**（合計 **${formatNumber(calc.gpu.totalVramGb)} GB VRAM**）＋ **${formatNumber(calc.cpu.cores)} 核心 CPU**（約 **${(calc.cpu.coreDays / 10000).toFixed(1)} 萬核·日/年**）\n`;
    md += `- **儲存減負成果**：磁帶嚴格執行 1.0 PB 封頂，磁碟縮減至 200 TB 純快取，徹底解除 ASGC 79% 儲存熔斷危機。\n\n`;

    md += `## 二、 四大核心板塊配置清單與經費明細表\n\n`;
    md += `| 板塊類別 | 項目規格與型號 | 官方單價基準 | 配置規模 | 年度支出 (NTD) | 預算佔比 |\n`;
    md += `| :--- | :--- | :--- | :--- | :--- | :--- |\n`;
    md += `| **1. 磁帶歸檔** | LTO 企業級磁帶庫 | NT$ 300 / TB / 年 | ${formatNumber(calc.tape.tb)} TB (${calc.tape.pb.toFixed(2)} PB) | NT$ ${formatNumber(calc.tape.cost)} | ${calc.tape.pct.toFixed(2)}% |\n`;
    md += `| **2. 磁碟快取** | CephFS 高速工作快取 | NT$ 1,000 / TB / 年 | ${formatNumber(calc.disk.tb)} TB (${calc.disk.pb.toFixed(2)} PB) | NT$ ${formatNumber(calc.disk.cost)} | ${calc.disk.pct.toFixed(2)}% |\n`;
    md += `| **3. OSDF 樞紐** | 快取磁碟 (XRootD) | NT$ 1,000 / TB / 年 | ${calc.osdf.diskTb} TB | NT$ ${formatNumber(calc.osdf.diskCost)} | ${((calc.osdf.diskCost / calc.totalBudget) * 100).toFixed(2)}% |\n`;
    md += `| **3. OSDF 樞紐** | 代理節點 vCPU (${calc.osdf.cpuType}) | NT$ ${calc.cpu.unitPrice} / 核·年 | ${calc.osdf.vcpu} vCPU | NT$ ${formatNumber(calc.osdf.cpuCost)} | ${((calc.osdf.cpuCost / calc.totalBudget) * 100).toFixed(2)}% |\n`;

    for (const g of calc.gpu.details) {
      md += `| **4. 核心算力** | GPU: ${g.name} | NT$ ${formatNumber(g.per_card_year)} / 卡·年 | ${g.count} 卡 (${g.vram_gb_total} GB VRAM) | NT$ ${formatNumber(g.total_cost)} | ${((g.total_cost / calc.totalBudget) * 100).toFixed(2)}% |\n`;
    }

    md += `| **4. 核心算力** | CPU: ${calc.cpu.name} | NT$ ${calc.cpu.unitPrice} / 核·年 | ${formatNumber(calc.cpu.cores)} 核心 | NT$ ${formatNumber(calc.cpu.cost)} | ${calc.cpu.pct.toFixed(2)}% |\n`;
    md += `| **合計總覽** | **四大板塊全案總計** | - | **${formatNumber(calc.tape.tb + calc.disk.tb + calc.osdf.diskTb)} TB / ${calc.gpu.totalCards} 卡 / ${formatNumber(calc.cpu.cores)} 核** | **NT$ ${formatNumber(calc.totalCost)}** | **${calc.utilizationPct.toFixed(2)}%** |\n\n`;

    md += `## 三、 戰略合規性與學術治理檢核\n\n`;
    md += `1. **DocDB 17608 政策合規**：${calc.tape.tb <= 1000 ? '✅ **完全合規**' : '⚠️ **超額**'}。磁帶保留容量控制於 1.0 PB 封頂，每半年經 CSWG 清單刪除非觀測期過渡工程數據。\n`;
    md += `2. **CephFS 儲存熔斷解除**：${calc.disk.tb <= 200 ? '✅ **解除危機**' : '⚠️ **建議壓減**'}。磁碟精簡至 200 TB 純計算工作快取，免除 ASGC 79% 儲存熔斷風險。\n`;
    md += `3. **GPU 算力優先轉型**：${calc.gpuTargetMet ? '✅ **戰略指標達成**' : '⚠️ **未達 65%**'}。專案專用 GPU 佔算力池核心，攻克 LVK 全球 GPU 短缺瓶頸。\n`;
    md += `4. **國際學術成果標註**：✅ **強制規範**。使用本算力產出之重力波論文一律致謝『Computed at Academia Sinica (AS)』。\n\n`;

    md += `## 四、 備忘錄簽核與執行附註\n\n`;
    md += `- **提案人**：陳孝宣 (Samson Chen) / 專案執行窗口\n`;
    md += `- **核定人**：王子敬教授 (Prof. Henry Wong) / KAGRA 台灣團隊 PI\n`;
    md += `- **受會單位**：ASGC 嚴漢偉主任、Felix Lee、翔宇 Patrick\n`;
    md += `- **國際對口**：東京大學宇宙線研究所（ICRR）澤田崇弘助教、大阪公立大學神田展行教授\n`;
  } else {
    // English / Japanese Brief
    md += `# KAGRA Budget Allocation & Computing Transformation Decision Brief\n\n`;
    md += `> **Date**: ${timestamp}  \n`;
    md += `> **Principal Investigator**: Prof. Henry Tsz-King Wong (ASIoP, Academia Sinica)  \n`;
    md += `> **Infrastructure Partner**: Academia Sinica Grid-computing Centre (ASGC)  \n`;
    md += `> **International Counterparts**: KAGRA Collaboration (Prof. Takahiro Sawada / Prof. Nobuyuki Kanda)  \n`;
    md += `> **Policy Compliance**: KAGRA DocDB 17608 (Approved unanimously at KSC on Oct 29, 2026)  \n`;
    md += `> **Pricing Benchmark**: Official NSTCCore / ASGC 2026 Public Tariffs  \n\n`;

    md += `## 1. Executive Summary\n\n`;
    md += `- **Total Annual Budget**: NT$ ${formatNumber(calc.totalBudget)} / year (~$46,000 USD / ~7,000,000 JPY)\n`;
    md += `- **Allocated Expenditure**: **NT$ ${formatNumber(calc.totalCost)}** / year (**${calc.utilizationPct.toFixed(2)}%** utilization)\n`;
    md += `- **Remaining Fiscal Balance**: NT$ ${formatNumber(calc.remainingBalance)} (${calc.isOptimal ? 'Optimal Zero-Waste Balance' : 'Surplus / Shortfall'})\n`;
    md += `- **GPU Strategic Investment Share**: **${calc.gpu.pct.toFixed(2)}%** (${calc.gpuTargetMet ? 'Target Met >65%' : 'Below Target'})\n`;
    md += `- **Compute Power Delivered**: **${calc.gpu.totalCards} Flagship GPUs** (**${formatNumber(calc.gpu.totalVramGb)} GB VRAM**) + **${formatNumber(calc.cpu.cores)} CPU Cores** (~${(calc.cpu.coreDays / 1000).toFixed(0)}k core-days/yr)\n`;
    md += `- **Storage Transformation**: 1.0 PB Tape cap strictly honored; CephFS downscaled to 200 TB high-speed scratch, mitigating ASGC's 79% storage threshold crisis.\n\n`;

    md += `## 2. Resource Allocation Breakdown Table\n\n`;
    md += `| Module | Item & Spec | Tariff Rate | Quota Allocated | Annual Cost (NTD) | Share % |\n`;
    md += `| :--- | :--- | :--- | :--- | :--- | :--- |\n`;
    md += `| **Tape Archive** | LTO Enterprise Tape | NT$ 300 / TB / yr | ${formatNumber(calc.tape.tb)} TB (${calc.tape.pb.toFixed(2)} PB) | NT$ ${formatNumber(calc.tape.cost)} | ${calc.tape.pct.toFixed(2)}% |\n`;
    md += `| **CephFS Scratch** | NVMe/SAS Scratch Cache | NT$ 1,000 / TB / yr | ${formatNumber(calc.disk.tb)} TB (${calc.disk.pb.toFixed(2)} PB) | NT$ ${formatNumber(calc.disk.cost)} | ${calc.disk.pct.toFixed(2)}% |\n`;
    md += `| **OSDF Hub** | Cache Disk | NT$ 1,000 / TB / yr | ${calc.osdf.diskTb} TB | NT$ ${formatNumber(calc.osdf.diskCost)} | ${((calc.osdf.diskCost / calc.totalBudget) * 100).toFixed(2)}% |\n`;
    md += `| **OSDF Hub** | Proxy vCPU (${calc.osdf.cpuType}) | NT$ ${calc.cpu.unitPrice} / core-yr | ${calc.osdf.vcpu} vCPU | NT$ ${formatNumber(calc.osdf.cpuCost)} | ${((calc.osdf.cpuCost / calc.totalBudget) * 100).toFixed(2)}% |\n`;

    for (const g of calc.gpu.details) {
      md += `| **Core Compute** | GPU: ${g.name} | NT$ ${formatNumber(g.per_card_year)} / card-yr | ${g.count} cards (${g.vram_gb_total} GB VRAM) | NT$ ${formatNumber(g.total_cost)} | ${((g.total_cost / calc.totalBudget) * 100).toFixed(2)}% |\n`;
    }

    md += `| **Core Compute** | CPU: ${calc.cpu.name} | NT$ ${calc.cpu.unitPrice} / core-yr | ${formatNumber(calc.cpu.cores)} Cores | NT$ ${formatNumber(calc.cpu.cost)} | ${calc.cpu.pct.toFixed(2)}% |\n`;
    md += `| **Total** | **All Modules Combined** | - | **${formatNumber(calc.tape.tb + calc.disk.tb + calc.osdf.diskTb)} TB / ${calc.gpu.totalCards} GPUs / ${formatNumber(calc.cpu.cores)} Cores** | **NT$ ${formatNumber(calc.totalCost)}** | **${calc.utilizationPct.toFixed(2)}%** |\n\n`;

    md += `## 3. Strategic Compliance & Governance Evaluation\n\n`;
    md += `1. **DocDB 17608 Policy Compliance**: ${calc.tape.tb <= 1000 ? 'Compliant' : 'Exceeded'} (Tape strictly capped at 1.0 PB)\n`;
    md += `2. **CephFS Scratch Scale**: ${calc.disk.tb <= 200 ? 'Safe' : 'Exceeded'} (Scratch capped at 200 TB, resolving 79% breaker crisis)\n`;
    md += `3. **GPU Priority Mandate**: ${calc.gpuTargetMet ? 'Target Met (>65%)' : 'Below 65%'} (Dedicated compute addressing global LVK GPU deficit)\n`;
    md += `4. **Attribution Standard**: Mandatory attribution 'Computed at Academia Sinica (AS)' on all scientific output.\n`;
  }

  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `kagra-decision-brief-${timestamp}.md`;
  a.click();
  URL.revokeObjectURL(url);

  showToast(t('actions.exported_notification', state.locale), 'info');
}

/* ==========================================================================
   DOM Event Bindings & App Bootstrap
   ========================================================================== */
function attachEventListeners() {
  // 1. Language switcher buttons
  document.querySelectorAll('.lang-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const targetLang = btn.dataset.lang;
      switchLocale(targetLang);
    });
  });

  // 2. Sliders
  const sliderTape = document.getElementById('slider-tape');
  const sliderDisk = document.getElementById('slider-disk');
  const sliderOsdfDisk = document.getElementById('slider-osdf-disk');
  const sliderOsdfVcpu = document.getElementById('slider-osdf-vcpu');
  const sliderCpuCores = document.getElementById('slider-cpu-cores');

  if (sliderTape) {
    sliderTape.addEventListener('input', (e) => {
      state.tape_tb = parseFloat(e.target.value);
      checkActivePreset();
      updateAnalytics();
      syncStateToUrl();
    });
  }

  if (sliderDisk) {
    sliderDisk.addEventListener('input', (e) => {
      state.disk_tb = parseFloat(e.target.value);
      checkActivePreset();
      updateAnalytics();
      syncStateToUrl();
    });
  }

  if (sliderOsdfDisk) {
    sliderOsdfDisk.addEventListener('input', (e) => {
      state.osdf_disk_tb = parseFloat(e.target.value);
      checkActivePreset();
      updateAnalytics();
      syncStateToUrl();
    });
  }

  if (sliderOsdfVcpu) {
    sliderOsdfVcpu.addEventListener('input', (e) => {
      state.osdf_vcpu = parseInt(e.target.value, 10);
      checkActivePreset();
      updateAnalytics();
      syncStateToUrl();
    });
  }

  if (sliderCpuCores) {
    sliderCpuCores.addEventListener('input', (e) => {
      state.cpu_cores = parseInt(e.target.value, 10);
      checkActivePreset();
      updateAnalytics();
      syncStateToUrl();
    });
  }

  // 3. Auto-fill CPU button
  const btnAutoFill = document.getElementById('btn-auto-fill-cpu');
  if (btnAutoFill) {
    btnAutoFill.addEventListener('click', () => {
      const optimalCores = calculateAutoFillCores(state);
      state.cpu_cores = optimalCores;
      if (sliderCpuCores) sliderCpuCores.value = optimalCores;
      checkActivePreset();
      updateAnalytics();
      syncStateToUrl();

      showToast(`${t('calculator.controls.auto_fill_cpu', state.locale)}: ${optimalCores} ${state.locale === 'en' ? 'Cores' : '核心'}`, 'info');
    });
  }

  // Budget Lock & Adjustment
  const btnBudgetLock = document.getElementById('btn-toggle-budget-lock');
  const inputCustomBudget = document.getElementById('input-custom-budget');
  const spanTotalBudget = document.getElementById('kpi-total-budget');

  if (btnBudgetLock) {
    btnBudgetLock.addEventListener('click', () => {
      state.isBudgetLocked = !state.isBudgetLocked;
      if (state.isBudgetLocked) {
        if (inputCustomBudget) inputCustomBudget.style.display = 'none';
        if (spanTotalBudget) spanTotalBudget.style.display = 'inline';
        btnBudgetLock.innerHTML = '<i data-lucide="lock" style="width: 14px; height: 14px; color: var(--color-cyan);"></i>';
      } else {
        if (inputCustomBudget) {
          inputCustomBudget.style.display = 'inline-block';
          inputCustomBudget.value = state.budget;
          inputCustomBudget.focus();
        }
        if (spanTotalBudget) spanTotalBudget.style.display = 'none';
        btnBudgetLock.innerHTML = '<i data-lucide="unlock" style="width: 14px; height: 14px; color: var(--color-amber);"></i>';
      }
      createIcons({ icons: usedIcons, root: btnBudgetLock });
    });
  }

  if (inputCustomBudget) {
    inputCustomBudget.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      if (Number.isFinite(val) && val > 0) {
        state.budget = val;
        checkActivePreset();
        updateAnalytics();
        syncStateToUrl();
      }
    });
    inputCustomBudget.addEventListener('blur', (e) => {
      let val = parseFloat(e.target.value);
      if (!Number.isFinite(val) || val < 100000) val = 100000;
      if (val > 20000000) val = 20000000;
      state.budget = val;
      e.target.value = val;
      checkActivePreset();
      updateAnalytics();
      syncStateToUrl();
    });
  }

  if (spanTotalBudget) {
    spanTotalBudget.addEventListener('click', () => {
      if (btnBudgetLock) btnBudgetLock.click();
    });
  }

  // Currency Badge Click Cycling (TWD -> USD -> JPY)
  const currencyBadge = document.getElementById('currency-badge');
  const currencyLabel = document.getElementById('currency-label');
  if (currencyBadge) {
    currencyBadge.addEventListener('click', () => {
      if (state.currency === 'TWD') state.currency = 'USD';
      else if (state.currency === 'USD') state.currency = 'JPY';
      else state.currency = 'TWD';

      if (currencyLabel) {
        currencyLabel.textContent = `${CURRENCY_RATES[state.currency].symbol} ${state.currency}`;
      }
      updateAnalytics();
      syncStateToUrl();
      const curToastMsg = state.locale === 'en' 
        ? `Currency switched to: ${state.currency}` 
        : (state.locale === 'ja' ? `通貨表示を切り替えました: ${state.currency}` : `貨幣顯示切換至: ${state.currency}`);
      showToast(curToastMsg, 'info');
    });
  }

  // 4. Share URL buttons
  const btnShareHeader = document.getElementById('btn-share-header');
  const btnCopyUrl = document.getElementById('btn-copy-url');

  const copyUrlHandler = () => {
    syncStateToUrl();
    navigator.clipboard.writeText(window.location.href).then(() => {
      showToast(t('actions.copied_notification', state.locale), 'link');
    }).catch(() => {
      showToast(window.location.href, 'link');
    });
  };

  if (btnShareHeader) btnShareHeader.addEventListener('click', copyUrlHandler);
  if (btnCopyUrl) btnCopyUrl.addEventListener('click', copyUrlHandler);

  // 5. Reset button
  const btnReset = document.getElementById('btn-reset');
  if (btnReset) {
    btnReset.addEventListener('click', () => {
      applyPreset('preset_1');
    });
  }

  // 6. Export buttons
  const btnExportJson = document.getElementById('btn-export-json');
  const btnExportMd = document.getElementById('btn-export-md');

  if (btnExportJson) btnExportJson.addEventListener('click', exportJSON);
  if (btnExportMd) btnExportMd.addEventListener('click', exportMarkdown);
}

/**
 * Main Initializer
 */
function init() {
  parseUrlState();
  renderPresets();
  renderGpuCards();
  renderCpuPills();
  renderFootnotes();
  updateSliderInputs();
  initCharts();
  attachEventListeners();
  switchLocale(state.locale);
  updateAnalytics();
  createIcons({ icons: usedIcons });
}

// Kick off when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
